import React, { useState, useCallback } from 'react';
import { useApolloClient } from '@apollo/client/react';
import {
  REQUEST_SETUP_MFA_SMS_TOTP,
  CONFIRM_MFA_SMS_TOTP,
  RequestSetupMfaSmsTotpResponse,
} from '../graphql/mfa';
import { phoneNumberSchema, otpCode6Schema } from '../schemas/mfaValidation';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ButtonComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DialogComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InputOTPComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InputOTPGroupComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InputOTPSlotComponentType = React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TextInputComponentType = React.ComponentType<any>;

export interface MfaSmsSetupProps {
  onSuccess: () => void;
  onCancel: () => void;
  DialogComponent: DialogComponentType;
  ButtonComponent: ButtonComponentType;
  InputOTPComponent: InputOTPComponentType;
  InputOTPGroupComponent: InputOTPGroupComponentType;
  InputOTPSlotComponent: InputOTPSlotComponentType;
  TextInputComponent: TextInputComponentType;
}

type SetupStep = 'phone' | 'verify';

export function MfaSmsSetup({
  onSuccess,
  onCancel,
  DialogComponent,
  ButtonComponent,
  InputOTPComponent,
  InputOTPGroupComponent,
  InputOTPSlotComponent,
  TextInputComponent,
}: MfaSmsSetupProps) {
  const client = useApolloClient();
  const [step, setStep] = useState<SetupStep>('phone');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [provisionRequestId, setProvisionRequestId] = useState<string | null>(null);
  const [code, setCode] = useState('');

  const sendCode = useCallback(
    async (phone: string) => {
      setLoading(true);
      setError(null);
      try {
        const result = (await client.mutate({
          mutation: REQUEST_SETUP_MFA_SMS_TOTP,
          variables: {
            input: { phone_number: phone },
          },
        })) as { data?: RequestSetupMfaSmsTotpResponse };
        const data = result.data?.requestSetupMfaSmsTotpForMe.mfaSmsTotpProvisionRequest;
        if (data) {
          setProvisionRequestId(data.id);
          setStep('verify');
        }
      } catch (err) {
        setError('Failed to send verification code. Please check the phone number and try again.');
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  const handleSendCode = useCallback(() => {
    const result = phoneNumberSchema.safeParse({
      phoneNumber: phoneNumber.replace(/[\s()-]/g, ''),
    });
    if (!result.success) {
      setError(result.error.issues[0].message);
      return;
    }
    sendCode(phoneNumber);
  }, [phoneNumber, sendCode]);

  const handleResend = useCallback(async () => {
    setResending(true);
    setError(null);
    setCode('');
    try {
      const result = (await client.mutate({
        mutation: REQUEST_SETUP_MFA_SMS_TOTP,
        variables: {
          input: { phone_number: phoneNumber },
        },
      })) as { data?: RequestSetupMfaSmsTotpResponse };
      const data = result.data?.requestSetupMfaSmsTotpForMe.mfaSmsTotpProvisionRequest;
      if (data) {
        setProvisionRequestId(data.id);
      }
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  }, [client, phoneNumber]);

  const handleConfirm = useCallback(async () => {
    if (!provisionRequestId) return;

    const result = otpCode6Schema.safeParse({ code });
    if (!result.success) {
      setError(result.error.issues[0].message);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await client.mutate({
        mutation: CONFIRM_MFA_SMS_TOTP,
        variables: {
          id: provisionRequestId,
          input: { auth_code: code },
        },
      });
      onSuccess();
    } catch (err) {
      setError('Invalid code. Please check and try again.');
      setCode('');
    } finally {
      setLoading(false);
    }
  }, [client, provisionRequestId, code, onSuccess]);

  const handleClose = useCallback(
    (open: boolean) => {
      if (!open) {
        onCancel();
      }
    },
    [onCancel]
  );

  const formatPhoneDisplay = (phone: string) => {
    const last4 = phone.slice(-4);
    return `***-***-${last4}`;
  };

  return (
    <DialogComponent isOpen={true} setIsOpen={handleClose} title="Set up SMS Verification" centered>
      <div className="flex flex-col gap-6 items-center mx-auto w-full max-w-md">
        {error && (
          <div className="w-full p-3 text-sm text-red-800 bg-red-100 dark:bg-red-900/30 dark:text-red-300 rounded-lg">
            {error}
          </div>
        )}

        {step === 'phone' && (
          <>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="flex justify-center items-center w-16 h-16 bg-blue-100 rounded-full dark:bg-blue-900">
                  <span className="text-3xl text-blue-600 material-symbols-outlined dark:text-blue-400">
                    sms
                  </span>
                </div>
              </div>
              <p className="text-grey-600 dark:text-grey-300">
                Enter your phone number to receive verification codes via SMS. Only US and Canadian
                numbers are supported.
              </p>
            </div>

            <div className="w-full text-center">
              <TextInputComponent
                label="Phone Number"
                placeholder="+1 (555) 123-4567"
                value={phoneNumber}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPhoneNumber(e.target.value)
                }
                disabled={loading}
                type="tel"
              />
            </div>

            <div className="flex flex-col gap-3 w-full">
              <ButtonComponent
                type="button"
                onClick={handleSendCode}
                disabled={!phoneNumber.trim() || loading}
                loading={loading}
              >
                Send Code
              </ButtonComponent>
              <ButtonComponent
                type="button"
                variant="tertiary"
                onClick={onCancel}
                disabled={loading}
              >
                Cancel
              </ButtonComponent>
            </div>
          </>
        )}

        {step === 'verify' && (
          <>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="flex justify-center items-center w-16 h-16 bg-blue-100 rounded-full dark:bg-blue-900">
                  <span className="text-3xl text-blue-600 material-symbols-outlined dark:text-blue-400">
                    sms
                  </span>
                </div>
              </div>
              <p className="text-grey-600 dark:text-grey-300">
                We sent a 6-digit verification code to{' '}
                <strong>{formatPhoneDisplay(phoneNumber)}</strong>. Enter the code below to enable
                SMS verification.
              </p>
            </div>

            <InputOTPComponent value={code} onChange={setCode} maxLength={6} disabled={loading}>
              <InputOTPGroupComponent>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <InputOTPSlotComponent key={i} index={i} />
                ))}
              </InputOTPGroupComponent>
            </InputOTPComponent>

            <div className="flex flex-col gap-3 w-full">
              <ButtonComponent
                type="button"
                onClick={handleConfirm}
                disabled={code.length !== 6 || loading}
                loading={loading}
              >
                Verify and Enable
              </ButtonComponent>
              <ButtonComponent
                type="button"
                variant="secondary"
                onClick={handleResend}
                disabled={resending || loading}
                loading={resending}
              >
                Resend Code
              </ButtonComponent>
              <ButtonComponent
                type="button"
                variant="tertiary"
                onClick={() => {
                  setStep('phone');
                  setCode('');
                  setError(null);
                }}
                disabled={loading || resending}
              >
                Use Different Number
              </ButtonComponent>
            </div>
          </>
        )}
      </div>
    </DialogComponent>
  );
}
